import { useEffect, useMemo, useRef, useState } from 'react';
import type { Arealplan } from '@origo/arealplan';
import { planKey } from '@origo/arealplan';
import type { PlanMetadata } from './services/PlanMetadataService';
import {
    TextInput,
    SearchResultCard,
    Chip,
    Notice,
    Loader,
    IconButton,
    Button,
    Dropdown,
} from '@origo/origo-ui';
import {
    MagnifyingGlassIcon,
    XIcon,
    WarningCircleIcon,
    FunnelIcon,
    ArrowsDownUpIcon,
} from '@phosphor-icons/react';
import { usePlanSearch } from './hooks/usePlanSearch';
import { usePlanFilter } from '../filter/usePlanFilter';
import { PlanFilterPanel } from '../filter/PlanFilterPanel';
import { formatDateLong, getPlanPublishStatus } from '../filter/filterUtils';
import { sortOptions, emptyFilters, type PlanFilters } from '../filter/types';
import styles from './PlanSearch.module.css';

interface Props {
    onSelect?: (plan: Arealplan) => void;
    /** Kalles når musen beveger seg inn på et kort — typisk for kart-forhåndsvisning */
    onHover?: (plan: Arealplan) => void;
    /** Kalles når musen forlater et kort */
    onLeave?: () => void;
    onZoomTo?: (plan: Arealplan) => void;
    /** PLANID som skal scrolles til og visuelt fokuseres — settes typisk når en plan klikkes på kartet */
    focusedPlanId?: string | null;
    /** PLANID under musepekeren på kartet — speiler hover-highlight fra kartet til listen */
    mapHoveredPlanId?: string | null;
    /** Kalles når avansert filter-kolonnen åpnes/lukkes — lar forelder justere panelbredden */
    onFilterToggle?: (open: boolean) => void;
    /** Publiseringsmetadata per plan — brukes for å vise status (ikke påbegynt / utkast / publisert) */
    planMetadata?: Map<string, PlanMetadata>;
}

const HOVER_DELAY_MS = 120;

const reducedMotion = (): boolean =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function filterByText(plans: Arealplan[], query: string): Arealplan[] {
    if (!query) return plans;
    const q = query.toLowerCase();
    return plans.filter((p) => {
        const navn = (p.planNavn ?? '').toLowerCase();
        const id = (p.planId ?? '').toLowerCase();
        // KOMMUNENAVN finnes ikke som domain-felt — det er ikke en del av
        // standard plan-list-attributtene. Hvis behov: utvid Arealplan.
        return navn.includes(q) || id.includes(q);
    });
}

export function PlanSearch({ onSelect, onHover, onLeave, onZoomTo, focusedPlanId, mapHoveredPlanId, onFilterToggle, planMetadata }: Readonly<Props>) {
    const { allPlans, loading, error } = usePlanSearch();

    const [query, setQuery] = useState('');
    const [filterOpen, setFilterOpen] = useState(false);

    const handleFilterToggle = (open: boolean) => {
        setFilterOpen(open);
        onFilterToggle?.(open);
    };
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [activePlanId, setActivePlanId] = useState<string | null>(null);

    // Kart-peker → speiler hover-highlight til kortlisten
    useEffect(() => {
        if (!mapHoveredPlanId) {
            onLeave?.();
            return;
        }
        const plan = allPlans.find((p) => p.planId === mapHoveredPlanId);
        if (plan) onHover?.(plan);
    }, [mapHoveredPlanId, allPlans, onHover, onLeave]);

    // Når en plan fokuseres via kartet — scroll tilhørende kort til midten
    useEffect(() => {
        if (!focusedPlanId) return;
        const plan = allPlans.find((p) => p.planId === focusedPlanId);
        if (!plan) return;
        const key = planKey(plan);
        setActivePlanId(key);
        const el = cardRefs.current[key];
        if (el) {
            // rAF gir nettleseren tid til å male kortet (content-visibility: auto) før
            // smooth-scroll-beregningen kjøres — unngår over-/undershooting.
            requestAnimationFrame(() => {
                el.scrollIntoView({
                    behavior: reducedMotion() ? 'auto' : 'smooth',
                    block: 'center',
                });
            });
        }
        onZoomTo?.(plan);
    }, [focusedPlanId, allPlans, onZoomTo]);

    const cancelHoverTimer = () => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    };

    const handleHover = (plan: Arealplan) => {
        if (!onHover) return;
        cancelHoverTimer();
        hoverTimerRef.current = setTimeout(() => onHover(plan), HOVER_DELAY_MS);
    };

    const handleLeave = () => {
        cancelHoverTimer();
        onLeave?.();
    };

    const preFilteredPlans = useMemo(() => filterByText(allPlans, query), [allPlans, query]);

    const {
        filters,
        setFilters,
        sortOption,
        setSortOption,
        filteredPlans,
        activeFilterCount,
    } = usePlanFilter({ plans: preFilteredPlans, planMetadata });

    const activeFilterChips = useMemo(() => {
        const chips: Array<{ id: string; label: string; onRemove: () => void }> = [];
        const patch = (p: Partial<PlanFilters>) => setFilters({ ...filters, ...p });

        filters.statuses.forEach((s) => {
            chips.push({
                id: `status-${s}`,
                label: `Status: ${s}`,
                onRemove: () => patch({ statuses: filters.statuses.filter((x) => x !== s) }),
            });
        });
        filters.types.forEach((t) => {
            chips.push({
                id: `type-${t}`,
                label: `Plantype: ${t}`,
                onRemove: () => patch({ types: filters.types.filter((x) => x !== t) }),
            });
        });
        filters.vertikalNivaer.forEach((v) => {
            chips.push({
                id: `niva-${v}`,
                label: `Nivå: ${v}`,
                onRemove: () => patch({ vertikalNivaer: filters.vertikalNivaer.filter((x) => x !== v) }),
            });
        });
        filters.innholdsStatuser.forEach((s) => {
            const label = s === 'publisert' ? 'Publisert' : s === 'utkast' ? 'Utkast' : 'Ikke påbegynt';
            chips.push({
                id: `innhold-${s}`,
                label: `Publiseringsstatus: ${label}`,
                onRemove: () => patch({ innholdsStatuser: filters.innholdsStatuser.filter((x) => x !== s) }),
            });
        });
        if (filters.dateFrom || filters.dateTo) {
            const from = filters.dateFrom ? formatDateLong(filters.dateFrom.getTime()) : '';
            const to = filters.dateTo ? formatDateLong(filters.dateTo.getTime()) : '';
            const label = from && to ? `${from} – ${to}` : (from ? `Fra ${from}` : `Til ${to}`);
            chips.push({
                id: 'date-range',
                label: `Ikrafttredelse: ${label}`,
                onRemove: () => patch({ dateFrom: null, dateTo: null }),
            });
        }
        if (filters.sectionFilter && filters.sectionFilter !== 'all') {
            chips.push({
                id: 'section',
                label: filters.sectionFilter === 'with-sections' ? 'Med seksjoner' : 'Uten seksjoner',
                onRemove: () => patch({ sectionFilter: 'all' }),
            });
        }
        return chips;
    }, [filters, setFilters]);

    return (
        <div className={styles.root}>
            <div className={styles.mainColumn}>
            <div className={styles.header}>
                <h1 className={styles.title}>Planer</h1>
                <p className={styles.subtitle}>Finn en reguleringsplan for å opprette eller redigere en drømmeplan.</p>
            </div>

            <div className={styles.stickyControls}>
                <div className={styles.searchRow}>
                    <TextInput
                        className={styles.searchField}
                        label="Søk etter plan"
                        hideLabel
                        placeholder="Plannavn, ID eller kommune"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        prefix={<MagnifyingGlassIcon size={16} />}
                        suffix={
                            query ? (
                                <IconButton
                                    icon={<XIcon size={14} />}
                                    label="Tøm søk"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setQuery('')}
                                />
                            ) : undefined
                        }
                    />
                    <Button
                        variant="secondary"
                        size="lg"
                        className={styles.filterButton}
                        onClick={() => handleFilterToggle(!filterOpen)}
                        aria-expanded={filterOpen}
                        aria-controls="plan-filter-column"
                    >
                        <FunnelIcon size={16} />
                        Filtrer
                        {activeFilterCount > 0 && (
                            <span className={styles.filterCountBadge} aria-label={`${activeFilterCount} aktive filter`}>
                                {activeFilterCount}
                            </span>
                        )}
                    </Button>
                    <Dropdown
                        align="end"
                        className={styles.sortDropdown}
                        trigger={
                            <span className={styles.sortTrigger} aria-label="Sorter planer">
                                <ArrowsDownUpIcon size={18} />
                            </span>
                        }
                        groups={[{
                            items: sortOptions.map((o) => ({
                                id: `${o.field}-${o.direction}`,
                                label: o.label,
                                selected: sortOption.field === o.field && sortOption.direction === o.direction,
                                onClick: () => setSortOption(o),
                            })),
                        }]}
                    />
                </div>

                <div className={styles.resultMeta}>
                    <span className={styles.resultCount}>
                        {filteredPlans.length}{'\u00A0'}planer
                    </span>
                    {activeFilterChips.length > 0 && (
                        <>
                            <div className={styles.filterChips}>
                                {activeFilterChips.map((c) => (
                                    <Chip
                                        key={c.id}
                                        onRemove={c.onRemove}
                                        removeLabel={`Fjern ${c.label}`}
                                    >
                                        {c.label}
                                    </Chip>
                                ))}
                            </div>
                            {activeFilterChips.length > 1 && (
                                <button
                                    type="button"
                                    className={styles.clearAll}
                                    onClick={() => setFilters(emptyFilters)}
                                >
                                    Tøm alle
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {loading && <Loader label="Laster planer..." className={styles.loader} />}

            {error && !loading && (
                <Notice variant="danger" icon={<WarningCircleIcon size={16} />} className={styles.emptyNotice}>
                    {error}
                </Notice>
            )}

            {!loading && !error && (
                filteredPlans.length === 0 ? (
                    <Notice variant="info" icon={<WarningCircleIcon size={16} />} className={styles.emptyNotice}>
                        Ingen planer matcher søket. Prøv å endre eller fjerne filter.
                    </Notice>
                ) : (
                    <div className={styles.results}>
                        {filteredPlans.map((plan) => {
                            const planNavn = plan.planNavn || 'Ukjent plan';
                            const planId = plan.planId;
                            const ikraftTs = plan.ikraft;
                            const key = planKey(plan);
                            const meta = planMetadata?.get(planId);
                            const status = getPlanPublishStatus(meta);
                            const sistRedigert = meta?.sistRedigertDato ? (() => {
                                const d = meta.sistRedigertDato;
                                const time = d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
                                return `${formatDateLong(d.getTime())} kl. ${time}`;
                            })() : null;
                            const subtitleText = [
                                ikraftTs ? formatDateLong(ikraftTs) : '',
                                sistRedigert ? `Sist redigert: ${sistRedigert}` : '',
                                meta?.publisert && meta.antallVisninger != null ? `${meta.antallVisninger} visninger` : '',
                            ]
                                .filter(Boolean)
                                .join('  ·  ');
                            const isFocused = activePlanId === key;
                            return (
                                <SearchResultCard
                                    key={key}
                                    ref={(el) => { cardRefs.current[key] = el; }}
                                    className={styles.resultItem}
                                    isFocused={isFocused}
                                    title={planNavn}
                                    subtitle={subtitleText}
                                    status={
                                        <Chip variant={status === 'publisert' ? 'success' : status === 'utkast' ? 'warning' : 'default'}>
                                            {status === 'publisert' ? 'Publisert' : status === 'utkast' ? 'Utkast' : 'Ikke påbegynt'}
                                        </Chip>
                                    }
                                    secondaryAction={
                                        onSelect && (
                                            <Button
                                                variant="secondary"
                                                size="md"
                                                onClick={() => onSelect(plan)}
                                            >
                                                Rediger
                                            </Button>
                                        )
                                    }
                                    onClick={() => {
                                        setActivePlanId(key);
                                        onZoomTo?.(plan);
                                    }}
                                    onMouseEnter={() => handleHover(plan)}
                                    onMouseLeave={handleLeave}
                                    onFocus={() => handleHover(plan)}
                                    onBlur={handleLeave}
                                />
                            );
                        })}
                    </div>
                )
            )}

            </div>

            {filterOpen && (
                <aside
                    id="plan-filter-column"
                    className={styles.filterColumn}
                    aria-label="Filtrer planer"
                >
                    <div className={styles.filterHeader}>
                        <h2 className={styles.filterTitle}>Filtrer</h2>
                        <IconButton
                            icon={<XIcon size={14} />}
                            label="Lukk filter"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleFilterToggle(false)}
                        />
                    </div>
                    <div className={styles.filterContent}>
                        <PlanFilterPanel
                            plans={allPlans}
                            filters={filters}
                            onFiltersChange={setFilters}
                            sortOption={sortOption}
                            onSortChange={setSortOption}
                            showSortControl={false}
                            planMetadata={planMetadata}
                        />
                    </div>
                </aside>
            )}
        </div>
    );
}
